import type { StudentMarks, Subject, SubjectDetails, TEventStatus, TEventType, TSlot } from '@assets/types/globals'
import { api } from '@store/api'
import type { TScheduleByWeekOrMonth, TUrgentTask } from '@store/student/types'
import {
  TLesson,
  TParentProfileForTeacher,
  TStudentProfileInfo,
  TTeacherClass,
  TTeacherEventDetail,
  TTeacherEventSubject,
  TTeacherProfileInfo,
  TTeacherScheduleByDay,
  TTeacherStudentsForEvent,
  TTeacherTopic,
  TeacherWorkloadDTO,
} from '@store/teacher/types'

export const teacherApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTeacherSubjects: builder.query<Subject[], any>({
      query: () => ({
        url: `/teacher/subjects/`,
      }),
    }),
    getTeacherSubjectById: builder.query<SubjectDetails, { id: string; quarter?: number }>({
      query: ({ id, quarter }) => ({
        url: `/teacher/subjects/${id}/`,
        params: {
          quarter,
        },
      }),
    }),
    getTeacherSubjectTopicById: builder.query<TTeacherTopic, { subjectId: string; topicId: string }>({
      query: ({ subjectId, topicId }) => ({
        url: `/teacher/subjects/${subjectId}/topic/${topicId}`,
      }),
      // transformResponse: (response: TTeacherTopic) => response.lessons,
    }),
    getTeacherSubjectMarks: builder.query<StudentMarks[], { subjectId: string }>({
      query: ({ subjectId }) => ({
        url: `/teacher/subjects/${subjectId}/get_all_marks/`,
      }),
    }),
    getTeacherScheduleByDay: builder.query<TTeacherScheduleByDay[], { start_date: string; end_date?: string; student_id?: string }>({
      query: ({ start_date, end_date, student_id }) => ({
        url: `/teacher/schedule/`,
        params: {
          start_date,
          end_date,
          student_id,
        },
      }),
    }),
    getTeacherSchedule: builder.query<TScheduleByWeekOrMonth[], { start_date: string; end_date?: string }>({
      query: ({ start_date, end_date }) => ({
        url: `/calendar_events/teachers/`,
        params: { start_date, end_date },
      }),
      providesTags: ['teacherScheduleByWeekOrMonth'],
    }),
    getTeacherStudentsForEvent: builder.query<TTeacherStudentsForEvent[], any>({
      query: () => ({
        url: `/calendar_events/teachers/get_students/`,
      }),
    }),
    getStudentSlotsForTeacher: builder.query<TSlot[], { date: string; students_ids: string[] }>({
      query: ({ date, students_ids }) => {
        const searchParams = new URLSearchParams()

        students_ids.forEach((s) => {
          searchParams.append('students_ids', s)
        })

        return {
          url: `/calendar_events/teachers/student_schedule/?${searchParams.toString()}`,
          params: { date },
        }
      },
      providesTags: ['studentSlotsForTeacher'],
    }),
    getTeacherEventSubjects: builder.query<TTeacherEventSubject, any>({
      query: (params) => ({
        url: 'calendar_events/teachers/subjects/',
        params,
      }),
    }),
    createTeacherEvent: builder.mutation<
      any,
      {
        event_type?: TEventType
        event_date?: string
        status: TEventStatus
        participants_ids?: Array<{
          participant_id: string
          personal_education_plan_link?: string
        }>
        description?: string
        slot?: number
        subject_id?: string
      }
    >({
      query: (body) => ({
        url: 'calendar_events/teachers/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['teacherScheduleByWeekOrMonth', 'studentSlotsForTeacher'],
    }),
    updateTeacherEvent: builder.mutation<
      any,
      {
        id: string
        event_type?: TEventType
        event_date?: string
        status?: TEventStatus
        participants_ids?: Array<string>
        description?: string
        slot?: number
        subject_id?: string
      }
    >({
      query: ({ id, ...body }) => ({
        url: `calendar_events/teachers/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['teacherScheduleByWeekOrMonth', 'studentSlotsForTeacher', 'teacherEventDetail'],
    }),
    setPersonalEducationPlanLink: builder.mutation<
      any,
      {
        id: string
        data?: Array<{
          participant_id: string
          personal_education_plan_link?: string
        }>
      }
    >({
      query: ({ id, data }) => ({
        url: `calendar_events/teachers/${id}/set-personal-education-plan-link/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['teacherEventDetail'],
    }),
    deleteTeacherEvent: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `calendar_events/teachers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['teacherScheduleByWeekOrMonth', 'studentSlotsForTeacher'],
    }),
    setTeacherEventAttendance: builder.mutation<any, { id: string; data: { participant_id: string; attendance_confirmed: boolean }[] }>({
      query: ({ id, data }) => ({
        url: `/api/v1/calendar_events/teachers/${id}/set-attendance/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['teacherEventDetail'],
    }),
    getTeacherEventById: builder.query<TTeacherEventDetail, { id: string }>({
      query: ({ id }) => ({
        url: `/calendar_events/teachers/${id}`,
      }),
      providesTags: ['teacherEventDetail'],
    }),
    getTeacherUrgentTasks: builder.query<TUrgentTask[], { student_id?: string }>({
      query: ({ student_id }) => ({
        url: `/teacher/notifications/urgent_task_for_teacher/`,
        params: { student_id },
      }),
    }),
    getTeacherMyClass: builder.query<TTeacherClass, { id: string }>({
      query: ({ id }) => ({
        url: `/teacher/my_class/${id}/`,
      }),
    }),
    getTeacherProfileInfo: builder.query<TTeacherProfileInfo, { teacher_id: string }>({
      query: ({ teacher_id }) => ({
        url: `/detail/teacher/${teacher_id}`,
      }),
    }),
    getStudentProfile: builder.query<TStudentProfileInfo, { student_id?: string }>({
      query: ({ student_id }) => ({
        url: `/users/student_card/${student_id}/`,
      }),
    }),
    getParentProfileForTeacher: builder.query<TParentProfileForTeacher, { parent_id?: string }>({
      query: ({ parent_id }) => ({
        url: `/users/parent_card/${parent_id}/`,
      }),
    }),
    getTeacherWorkload: builder.query<TeacherWorkloadDTO, { teacher_id?: string; start_date: string; end_date: string }>({
      query: (params) => ({
        url: '/externat/workload/',
        params,
      }),
    }),
  }),
})

export const {
  useGetTeacherSubjectsQuery,
  useGetTeacherSubjectByIdQuery,
  useGetTeacherSubjectTopicByIdQuery,
  useGetTeacherSubjectMarksQuery,
  useGetTeacherScheduleByDayQuery,
  useGetTeacherScheduleQuery,
  useGetTeacherStudentsForEventQuery,
  useGetStudentSlotsForTeacherQuery,
  useGetTeacherEventSubjectsQuery,
  useLazyGetTeacherEventSubjectsQuery,
  useCreateTeacherEventMutation,
  useUpdateTeacherEventMutation,
  useDeleteTeacherEventMutation,
  useSetPersonalEducationPlanLinkMutation,
  useSetTeacherEventAttendanceMutation,
  useGetTeacherEventByIdQuery,
  useGetTeacherUrgentTasksQuery,
  useGetTeacherMyClassQuery,
  useGetTeacherProfileInfoQuery,
  useGetStudentProfileQuery,
  useGetParentProfileForTeacherQuery,
  useGetTeacherWorkloadQuery,
} = teacherApi
